import {
  List,
  IconButton,
  TextField,
  Box,
  Avatar,
  Icon,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  MenuItem,
  Select,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import React, { useEffect, useMemo, useRef, useState } from "react";
import MDTypography from "components/MDTypography";
import { application } from "globals/endpoints";
import loggerFactory from "globals/logger/logger-factory";
import noList from "../../../assets/images/noList.png";
import CTLBatteryStatus from "application/components/CTLBatteryStatus";
import SignalCellularConnectedNoInternet0BarIcon from "@mui/icons-material/SignalCellularConnectedNoInternet0Bar";
import { setRTCEvent } from "context";
import MDButton from "components/MDButton";
import V3MediaPTT from "libs/V3MediaPTT";
import Draggable from "react-draggable";
import Paper from "@mui/material/Paper";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import AuthenticationService from "../authentication/AuthenticationService";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import { alpha } from "@mui/material/styles";

const DraggablePaper = (props) => {
  const isMaximized = props["data-maximized"] === "true";

  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], .dialog-action-btn'}
      disabled={isMaximized}
    >
      <Paper
        {...props}
        sx={{
          resize: isMaximized ? "none" : "both",
          overflow: "hidden",
          borderRadius: isMaximized ? 0 : 2,
          minWidth: 400,
          minHeight: 300,
          maxWidth: "95vw",
          maxHeight: "95vh",
          width: 600,
          height: 500,
          touchAction: "manipulation",
          ...props.sx,
        }}
      />
    </Draggable>
  );
};

const Attributes = {
  modes: [],
};

const PAGE_SIZE = 8;

const SORT_OPTIONS = [
  { value: "department", label: "Department", type: "string" },
  { value: "extension", label: "Extension", type: "string" },
];

const emptyFallback = (value, fallback = "--") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return value;
};

function ActiveConference({ onLoad, pageId, searchText, registerCallBacks }) {
  const department = AuthenticationService.getDepartmentList() || [];
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, darkMode } = controller;
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState("department");
  const [sortDirection, setSortDirection] = useState("asc");

  const [selectedConference, setSelectedConference] = useState(null);
  const [conferenceUser, setConferenceUser] = useState([]);
  const [search, setSearch] = useState("");

  const [playDialogOpen, setPlayDialogOpen] = useState(false);
  const [playingStreamId, setPlayingStreamId] = useState(null);
  const antRef = useRef(null);
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [playingRoom, setPlayingRoom] = useState("");
  const [isDialogMaximized, setIsDialogMaximized] = useState(false);

  const toggleDialogMaximize = (e) => {
    e && e.stopPropagation();
    setIsDialogMaximized((prev) => !prev);
  };

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks && registerCallBacks({});
  }, [activePage]);

useEffect(() => {
  const fetchList = async () => {
    try {
      const payload = {
        searchRequest: {},
        departmentList: department.map((dept) => dept.department),
      };
      const response = await application.post(
        `registeredExtension/confSummary`,
        payload,
      );
      setData(response?.data?.data);
    } catch (error) {
      loggerFactory.debug("Unable to get active conference list", error);
      setData([]);
    }
  };

  fetchList();

  const interval = setInterval(fetchList, 3000);

  return () => clearInterval(interval);
}, []);

  useEffect(() => {
    if (selectedConference) {
      const fetchUsers = async () => {
        try {
          const response = await application.post(
            `registeredExtension/list/${selectedConference.extension}`,
          );
          setConferenceUser(response.data.data || []);
        } catch (error) {
          setConferenceUser([]);
          loggerFactory.debug(
            "Unable to get selected conference user list",
            error,
          );
        }
      };

      fetchUsers();
      const interval = setInterval(fetchUsers, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedConference]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchText, sortBy, sortDirection, data]);

  const processedConferenceList = useMemo(() => {
    const query = (searchText || "").trim().toLowerCase();

    const filtered = query
      ? data.filter((item) => {
          const fields = [
            item?.department,
            item?.extension,
            item?.name,
            item?.data?.pttControlWith,
            item?.data?.streamTakenBy,
          ];

          return fields.some((value) =>
            (value || "").toString().toLowerCase().includes(query),
          );
        })
      : data;

    const sorted = [...filtered].sort((left, right) => {
      const option = SORT_OPTIONS.find((item) => item.value === sortBy);
      const isNumber = option?.type === "number";

      const leftValue =
        sortBy === "totalNumber"
          ? left?.data?.totalNumber
          : sortBy === "inPtt"
            ? left?.data?.inPtt
            : left?.[sortBy];
      const rightValue =
        sortBy === "totalNumber"
          ? right?.data?.totalNumber
          : sortBy === "inPtt"
            ? right?.data?.inPtt
            : right?.[sortBy];

      if (isNumber) {
        const leftNumber = Number(leftValue || 0);
        const rightNumber = Number(rightValue || 0);

        return sortDirection === "asc"
          ? leftNumber - rightNumber
          : rightNumber - leftNumber;
      }

      const leftText = (leftValue || "").toString().toLowerCase();
      const rightText = (rightValue || "").toString().toLowerCase();

      return sortDirection === "asc"
        ? leftText.localeCompare(rightText)
        : rightText.localeCompare(leftText);
    });

    return sorted;
  }, [data, searchText, sortBy, sortDirection]);

  const totalRecords = processedConferenceList.length;
  const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / PAGE_SIZE) : 0;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      setCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [currentPage, totalPages]);

  const paginatedConferenceList = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    return processedConferenceList.slice(start, start + PAGE_SIZE);
  }, [currentPage, processedConferenceList]);

  const canGoPrevious = currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;

  const panelGradient = darkMode
    ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
    : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)";

  const handleOpenMenu = (conference) => {
    setRTCEvent(dispatch, {
      value: conference.department,
      type: "GroupCall",
      data: conference.extension,
    });
    setSelectedConference(conference);
  };

  const handleClose = () => {
    setRTCEvent(dispatch, null);
    setSelectedConference(null);
    setConferenceUser([]);
    setSearch("");
  };

  const domain = AuthenticationService.getXmppPrimaryDomain();
  const serverUrl =
    domain && domain.trim() !== ""
      ? `${domain}/LiveApp/websocket`
      : "wss://conf.coraltele.com:5443/LiveApp/websocket";

  const openPlayDialog = async (e, streamId) => {
    e && e.stopPropagation();
    if (playDialogOpen && playingStreamId === streamId) return;

    setIsDialogMaximized(false);
    setPlayDialogOpen(true);
    setPlayingStreamId(streamId);
    try {
      const websocketUrl = serverUrl;
      const roomId = "default";
      const onRemoteTrack = ({ stream, streamId: remoteStreamId }) => {
        if (!stream || !videoRef.current) return;
        try {
          const videoTracks =
            (stream.getVideoTracks && stream.getVideoTracks()) || [];
          const videoOnlyStream = new MediaStream(videoTracks);
          videoRef.current.onloadeddata = () => setIsVideoLoaded(true);
          videoRef.current.srcObject = videoOnlyStream;
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {});
        } catch (err) {
          console.error("Error attaching remote video stream", err);
        }
      };

      const ant = new V3MediaPTT({
        websocketUrl,
        streamId: "dummy-pub-" + Date.now().toString(36),
        roomId,
        onRemoteTrack,
        dataChannelEnabled: true,
      });

      antRef.current = ant;
      await ant.init();
      if (typeof ant._createPlayAdaptor === "function") {
        ant._createPlayAdaptor(streamId);
      } else {
        console.error("V3 doesn't provide _createPlayAdaptor.");
      }
    } catch (e) {
      console.error("Failed to start play stream:", e);
    }
  };

  const stopPlayAndCleanup = async () => {
    const ant = antRef.current;
    const sid = playingStreamId;
    try {
      if (ant && typeof ant._stopPlayStream === "function") {
        ant._stopPlayStream(sid);
      }
      if (ant && typeof ant.close === "function") {
        await ant.close();
      }
    } catch (e) {
      console.warn("Error stopping play stream", e);
    } finally {
      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch (e) {}
        videoRef.current.srcObject = null;
      }
      antRef.current = null;
      setPlayDialogOpen(false);
      setPlayingStreamId(null);
      setPlayingRoom("");
      setIsDialogMaximized(false);
      setIsVideoLoaded(false);
    }
  };

  useEffect(() => {
    return () => {
      if (antRef.current) {
        try {
          antRef.current.close();
        } catch (e) {}
        antRef.current = null;
      }
    };
  }, []);

  const filterParticipants = (statusFilter) =>
    conferenceUser
      ?.filter(statusFilter)
      ?.filter(
        (user) =>
          user?.participantNo?.toString().includes(search) ||
          user?.designation?.toLowerCase().includes(search.toLowerCase()),
      );

  const registeredInConference = filterParticipants(
    (user) =>
      user.registeredStatus === "Registered" &&
      user.conferenceStatus !== "Not In Conference" &&
      user.conferenceStatus !== "In Private Call",
  );
  const registeredInPrivateCall = filterParticipants(
    (user) =>
      user.registeredStatus === "Registered" &&
      user.conferenceStatus === "In Private Call",
  );
  const registeredInNotConference = filterParticipants(
    (user) =>
      user.registeredStatus === "Registered" &&
      user.conferenceStatus === "Not In Conference",
  );
  const notRegisteredInNotConference = filterParticipants(
    (user) => user.registeredStatus !== "Registered",
  );

  const ParticipantItem = ({ participant }) => (
    <Box sx={{ minWidth: 0 }}>
      <MDBox
        display="flex"
        alignItems="center"
        sx={{
          position: "relative",
          minHeight: 52,
          px: 0.85,
          py: 0.55,
          gap: 0.7,
          borderRadius: 1.4,
          // bgcolor: darkMode
          //   ? alpha("#0f172a", 0.56)
          //   : alpha("#ffffff", 0.9),
          border: (theme) =>
            `1px solid ${alpha(
              participant.registeredStatus === "Registered"
                ? theme.palette.success.main
                : theme.palette.error.main,
              0.28,
            )}`,
          boxShadow: darkMode
            ? "0 5px 14px rgba(0,0,0,0.32)"
            : "0 4px 12px rgba(15,23,42,0.12)",
          transition: "transform 160ms ease, box-shadow 160ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: darkMode
              ? "0 8px 18px rgba(0,0,0,0.36)"
              : "0 8px 18px rgba(15,23,42,0.16)",
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor:
              participant.registeredStatus === "Registered"
                ? "#00C853"
                : "#E53935",
            color: "white !important",
            width: 26,
            height: 26,
            fontSize: 11,
            fontWeight: 700,
            boxShadow: "0 3px 8px rgba(0,0,0,0.25)",
          }}
        >
          {(participant.designation || participant.participantNo || "N")
            .toString()
            .charAt(0)
            .toUpperCase()}
        </Avatar>

        <MDBox
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.1,
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              fontSize: "0.64rem",
              lineHeight: 1.2,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {participant.designation || participant.participantNo}
          </MDTypography>

          <MDTypography
            variant="caption"
            sx={{
              fontSize: "0.56rem",
              opacity: 0.74,
              lineHeight: 1.15,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {`Ext: ${emptyFallback(participant.participantNo)}`}
          </MDTypography>

          {participant.registeredStatus === "UnRegistered" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
              {participant?.rsRpSim1 < -120 && (
                <SignalCellularConnectedNoInternet0BarIcon
                  fontSize="small"
                  sx={{ fontSize: 12, strokeWidth: 1.5 }}
                  color="error"
                />
              )}
              <MDTypography variant="caption" sx={{ fontSize: "0.52rem", opacity: 0.72 }}>
                {`RSRP1: ${participant.rsRpSim1 || "NA"}`}
              </MDTypography>
            </Box>
          )}
        </MDBox>

        <Box
          sx={{
            minWidth: 66,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            whiteSpace: "nowrap",
            "& .MuiTypography-root": {
              display: "inline-flex",
              alignItems: "center",
              gap: 0.2,
              fontSize: "0.52rem",
              lineHeight: 1,
              whiteSpace: "nowrap",
            },
            "& .MuiIconButton-root": {
              p: 0,
              mr: 0.1,
            },
            "& .MuiSvgIcon-root": {
              fontSize: 16,
            },
          }}
        >
          <CTLBatteryStatus value={participant.batteryPercentage}  />
        </Box>
      </MDBox>
    </Box>
  );

  if (selectedConference) {
    return (
      <Card
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: (theme) =>
            `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
          borderRadius: 1.5,
          // background: panelGradient,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            p: 0.65,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.6,
            flexWrap: "wrap",
            borderBottom: (theme) =>
              `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            bgcolor: (theme) =>
              alpha(theme.palette.background.default, darkMode ? 0.25 : 0.8),
            backdropFilter: "blur(6px)",
          }}
        >
          <IconButton
            onClick={handleClose}
            color="text"
            size="small"
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Icon>arrow_back</Icon>
          </IconButton>

          <Chip
            size="small"
            label={`Users: ${selectedConference?.data?.totalNumber || 0}`}
            sx={{
              height: 20,
              fontSize: "0.58rem",
              fontWeight: 700,
              borderRadius: 1,
              color: "#fff",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
            }}
          />
          <Chip
            size="small"
            label={`Joined Users: ${registeredInConference.length || 0}`}
            sx={{
              height: 20,
              fontSize: "0.58rem",
              fontWeight: 700,
              borderRadius: 1,
              color: "#fff",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            }}
          />
          <Chip
            size="small"
            label={`Not Joined Users: ${notRegisteredInNotConference.length || 0}`}
            sx={{
              height: 20,
              fontSize: "0.58rem",
              fontWeight: 700,
              borderRadius: 1,
              color: "#fff",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            }}
          />
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{
              flex: 1,
              minWidth: 120,
              fontSize: "0.68rem",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {`${selectedConference?.department} - (${selectedConference?.extension})`}
          </MDTypography>

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 170 },
              "& .MuiInputBase-root": {
                height: 30,
                fontSize: "0.62rem",
              },
              "& .MuiInputBase-input": {
                py: 0.45,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            p: 0.75,
            bgcolor: (theme) =>
              alpha(theme.palette.background.default, darkMode ? 0.2 : 0.65),
            display: "flex",
            flexDirection: "column",
            gap: 0.55,
          }}
        >
          {/* PRIVATE CALL */}
          {registeredInPrivateCall.length > 0 && (
            <Section
              title="In Private Call Participants"
              total={registeredInPrivateCall.length}
            >
              {registeredInPrivateCall.map((p, i) => (
                <ParticipantItem key={i} participant={p} />
              ))}
            </Section>
          )}

          {/* JOINED CONFERENCE */}
          {registeredInConference.length > 0 && (
            <Section
              title="Joined Conference Participants"
              total={registeredInConference.length}
            >
              {registeredInConference.map((p, i) => (
                <ParticipantItem key={i} participant={p} />
              ))}
            </Section>
          )}

          {/* NOT JOINED */}
          {registeredInNotConference.length > 0 && (
            <Section
              title="Not Joined Conference Participants"
              total={registeredInNotConference.length}
            >
              {registeredInNotConference.map((p, i) => (
                <ParticipantItem key={i} participant={p} />
              ))}
            </Section>
          )}

          {/* NOT REGISTERED */}
          {notRegisteredInNotConference.length > 0 && (
            <Section
              title="Not Registered Participants"
              total={notRegisteredInNotConference.length}
            >
              {notRegisteredInNotConference.map((p, i) => (
                <ParticipantItem key={i} participant={p} />
              ))}
            </Section>
          )}
        </Box>
      </Card>
    );
  }

  return (
    <MDBox mt={0} sx={{ height: "100%", minHeight: 0 }}>
      <Card
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          boxShadow: (theme) =>
            darkMode
              ? `0 4px 24px ${alpha(theme.palette.common.black, 0.4)}`
              : `0 2px 16px ${alpha(theme.palette.info.main, 0.07)}`,
          p: 0.85,
          gap: 0.7,
          background: (theme) =>
            darkMode
              ? `linear-gradient(160deg, ${alpha(theme.palette.background.default, 0.98)} 0%, ${alpha(theme.palette.info.dark, 0.08)} 100%)`
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.light, 0.06)} 100%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            flexWrap: "nowrap",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Chip
            label={`Total Conferences: ${totalRecords}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{
              height: 18,
              fontSize: "0.58rem",
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.8 },
            }}
          />

          <MDTypography
            variant="caption"
            sx={{
              fontSize: "0.63rem",
              fontWeight: 600,
              opacity: 0.7,
              flexShrink: 0,
            }}
          >
            Sort By
          </MDTypography>

          <Select
            size="small"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: 125,
              height: 26,
              fontSize: 11,
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                height: 26,
                py: 0,
                pr: 2.5,
                pl: 0.8,
              },
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: 12 }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Tooltip
            title={sortDirection === "desc" ? "Descending" : "Ascending"}
          >
            <IconButton
              size="small"
              onClick={() =>
                setSortDirection((previous) =>
                  previous === "desc" ? "asc" : "desc",
                )
              }
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                borderRadius: 1.5,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              {sortDirection === "desc" ? (
                <ArrowDownwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
              ) : (
                <ArrowUpwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
              )}
            </IconButton>
          </Tooltip>

          <MDTypography variant="caption" color="text" sx={{ opacity: 0.5, fontSize: "0.58rem", flexShrink: 0 }}>
            Auto refresh: 3s
          </MDTypography>

          {searchText ? (
            <Chip
              size="small"
              label={`Search: ${searchText}`}
              sx={{
                height: 18,
                fontSize: "0.58rem",
                maxWidth: 160,
                flexShrink: 0,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  px: 0.8,
                },
              }}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 1.2,
            border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            p: 0.55,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {paginatedConferenceList.length === 0 ? (
            <Box
              sx={{
                minHeight: 220,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
              }}
            >
              <img src={noList} height={120} alt="Empty List" />
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.75 }}>
                There are no active conference at the moment
              </MDTypography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: 0.55,
              }}
            >
              {paginatedConferenceList.map((item) => {
                const streamStarted = item?.data?.streamStatus === true || item?.data?.streamStatus === "true";
                const streamBy = item?.data?.streamTakenBy || "NA";

                return (
                  <Card
                    key={item?.id || item?.extension}
                    onClick={() => handleOpenMenu(item)}
                    sx={{
                      border: (theme) =>
                        `2px solid ${alpha(theme.palette.info.main, 0.18)}`,
                      borderRadius: 2,
                      borderColor: item?.color ? alpha(item.color, 0.5) : "transparent",
                      px: 0.58,
                      py: 0.5,
                      minHeight: 60,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      gap: 0.25,
                      cursor: "pointer",
                      // background: panelGradient,
                      position: "relative",
                      overflow: "hidden",
                      "&:before": {
                        content: '"   "',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        backgroundColor: item?.color || "transparent",
                      },
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: (theme) =>
                          `0 8px 18px ${alpha(theme.palette.common.black, 0.22)}`,
                      },
                      transition: "all 0.18s ease",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                          fontSize: "0.68rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          pl: 0.45,
                        }}
                      >
                        {emptyFallback(item?.department, "No Department")}
                      </MDTypography>
                      <Chip
                        size="small"
                        label={emptyFallback(item?.extension)}
                        sx={{
                          height: 16,
                          fontSize: "0.60rem",
                          color: (theme) => theme.palette.primary.contrastText,
                          backgroundColor: item?.color,
                        }}
                      />



                        <MDButton
                        variant="contained"
                        disabled={!streamStarted}
                        onClick={(event) => {
                          openPlayDialog(event, item?.data?.streamID);
                          setIsVideoLoaded(false);
                          setPlayingRoom(item);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.70rem",
                          fontWeight: 700,
                          px: 0.65,
                          py: 0.1,
                          minHeight: 25,
                          borderRadius: "999px",
                          color: "#fff",
                          background: streamStarted
                            ? "linear-gradient(90deg, #00C853, #43A047)"
                            : "linear-gradient(90deg, #BDBDBD, #757575)",
                          boxShadow: streamStarted
                            ? "0 4px 12px rgba(0, 200, 83, 0.4)"
                            : "0 2px 6px rgba(0,0,0,0.2)",
                        }}
                      >
                        {streamStarted
                          ? `Stream ${streamBy.length > 7 ? `${streamBy.substring(0, 7)}...` : streamBy}`
                          : "No Stream"}
                      </MDButton>
                    </Box>

                    <Box
                      sx={{
                        height: "1px",
                        mx: -0.58,
                        my: 0.1,
                        background: (theme) =>
                          `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.6)}, transparent)`,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        pl: 0.45,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                       
                        sx={{ fontSize: "0.64rem", opacity: 0.82 }}
                      >
                        {`Total: ${item?.data?.totalNumber || 0}`}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                       
                        sx={{ fontSize: "0.64rem", opacity: 0.82 }}
                      >
                        {`In PTT: ${item?.data?.inPtt || 0}`}
                      </MDTypography>

                       <MDTypography
                        variant="caption"
                     
                        sx={{ fontSize: "0.64rem", opacity: 0.82 }}
                      >
                        {`PTT: ${emptyFallback(item?.data?.pttControlWith, "Idle")}`}
                      </MDTypography>
                    </Box>

                    {/* <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.4,
                        pl: 0.45,
                      }}
                    >
                
                      <MDButton
                        variant="contained"
                        disabled={!streamStarted}
                        onClick={(event) => {
                          openPlayDialog(event, item?.data?.videoStreamId);
                          setIsVideoLoaded(false);
                          setPlayingRoom(item);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.70rem",
                          fontWeight: 700,
                          px: 0.65,
                          py: 0.1,
                          minHeight: 18,
                          borderRadius: "999px",
                          color: "#fff",
                          background: streamStarted
                            ? "linear-gradient(90deg, #00C853, #43A047)"
                            : "linear-gradient(90deg, #BDBDBD, #757575)",
                          boxShadow: streamStarted
                            ? "0 4px 12px rgba(0, 200, 83, 0.4)"
                            : "0 2px 6px rgba(0,0,0,0.2)",
                        }}
                      >
                        {streamStarted
                          ? `Stream ${streamBy.length > 7 ? `${streamBy.substring(0, 7)}...` : streamBy}`
                          : "No Stream"}
                      </MDButton>
                    </Box> */}
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            pt: 0.2,
            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: "0.6rem" }}>
            Page {totalPages === 0 ? 0 : currentPage + 1} / {totalPages}
          </MDTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <IconButton
              size="small"
              color="text"
              disabled={!canGoPrevious}
              onClick={() => setCurrentPage((previous) => Math.max(previous - 1, 0))}
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoPrevious ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateBeforeOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <IconButton
              size="small"
              color="text"
              disabled={!canGoNext}
              onClick={() =>
                setCurrentPage((previous) =>
                  totalPages > 0 ? Math.min(previous + 1, totalPages - 1) : previous,
                )
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoNext ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateNextOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <Dialog
        open={playDialogOpen}
        onClose={() => stopPlayAndCleanup()}
        PaperComponent={DraggablePaper}
        PaperProps={{
          "data-maximized": isDialogMaximized ? "true" : "false",
          sx: isDialogMaximized
            ? {
                width: "100vw !important",
                height: "100vh !important",
                maxWidth: "100vw !important",
                maxHeight: "100vh !important",
                minWidth: "100vw",
                minHeight: "100vh",
                m: 0,
                borderRadius: 0,
                position: "fixed",
                left: "0 !important",
                top: "0 !important",
                transform: "none !important",
              }
            : {
                width: { xs: "95vw", sm: 800 },
                height: { xs: "62vh", sm: 600 },
              },
        }}
        aria-labelledby="draggable-dialog-title"
        hideBackdrop={false}
        fullWidth={false}
        maxWidth={false}
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            touchAction: "manipulation",
          },
        }}
      >
        <DialogTitle
          id="draggable-dialog-title"
          sx={(theme) => ({
            ...CTLDialogContent(theme, { darkMode: controller.darkMode }),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: isDialogMaximized ? "default" : "move",
            p: "4px 8px",
          })}
        >
          <MDTypography variant="h6" sx={{ ml: 1 }}>
            {playingStreamId
              ? `Playing Stream: ${playingRoom?.department || playingStreamId}`
              : "Play Stream"}
          </MDTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              className="dialog-action-btn"
              aria-label={isDialogMaximized ? "restore" : "maximize"}
              color="inherit"
              onClick={toggleDialogMaximize}
              onTouchStart={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              size="medium"
              sx={{ width: 40, height: 40, touchAction: "manipulation" }}
            >
              {isDialogMaximized ? (
                <FullscreenExit fontSize="small" />
              ) : (
                <Fullscreen fontSize="small" />
              )}
            </IconButton>

            <IconButton
              className="dialog-action-btn"
              aria-label="close"
              color="error"
              onClick={() => stopPlayAndCleanup()}
              onTouchStart={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              size="medium"
              sx={{ width: 40, height: 40, touchAction: "manipulation" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={(theme) => ({
            ...CTLDialogContent(theme, { darkMode: controller.darkMode }),
            position: "relative",
            display: "flex",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "black",
            p: 0,
          })}
        >
          {/* Loading overlay */}
          {!isVideoLoaded && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: "white",
                background: "rgba(0,0,0,0.4)",
                zIndex: 2,
              }}
            >
              <CircularProgress color="inherit" size={40} sx={{ mb: 2 }} />
              <MDTypography variant="h6">Playing stream...</MDTypography>
            </Box>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            onLoadedData={() => setIsVideoLoaded(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              backgroundColor: "#000",
              borderRadius: "4px",
            }}
          />
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}

const Section = ({ title, total, children }) => (
  <Box mt={0.35}>
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      px={0.55}
      py={0.35}
      sx={{
        borderRadius: 1,
        border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.24)}`,
        background: (theme) => alpha(theme.palette.info.main, 0.09),
      }}
    >
      <MDTypography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontSize: "0.63rem",
          lineHeight: 1.15,
          mr: 1,
        }}
      >
        {title}
      </MDTypography>
      <MDTypography
        variant="caption"
        sx={{
          height: 17,
          fontSize: "0.55rem",
          fontWeight: 700,
          borderRadius: 1,
        }}
      >
        {`Total ${total}`}
      </MDTypography>
    </MDBox>

    <List
      disablePadding
      sx={{
        mt: 0.35,
        border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        borderRadius: 1.1,
        p: 0.5,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 0.75,
          width: "100%",
          maxWidth: 1240,
          mx: "auto",
        }}
      >
        {children}
      </Box>
    </List>
  </Box>
);

export default ActiveConference;
